import {useEffect, useSyncExternalStore} from 'react';

/**
 * Coordinates RHS OverlayPanelLayout/ResizablePanelLayout instances across the
 * app so only one is ever visibly open. Each instance is self-contained by
 * design (see OverlayPanelLayout.tsx) with no knowledge of sibling instances;
 * this module is the cross-cutting arbiter. `GlobalCreateFab`'s file drawer is
 * portaled to document.body and mounted outside any page's subtree, so a
 * React Context wouldn't buy anything a plain module singleton doesn't already
 * give for free — claim() evicts the previous owner by calling its stored
 * close callback directly, no render/subscription needed for that half.
 *
 * Currently wired into: GlobalCreateFab's FileViewDrawer, LibraryStream's
 * chat-tab RHS, and LibraryContainer's outer (Browse-mode) RHS — the three
 * surfaces that can concretely conflict while the global FAB is visible.
 * Other panel-owning pages (Otto, Lattice, Scout, Inbox, Document, Records,
 * Training, admin) can adopt the same primitive later via the identical
 * small effect below.
 *
 * To adopt in a new panel-owning component:
 *   useOverlayPanelClaim('my-panel-id', isOpen, closeSelf);
 */

type CloseFn = () => void;
let currentOwner: {id: string; close: CloseFn} | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

export function claimOverlayPanel(id: string, close: CloseFn): void {
  if (currentOwner && currentOwner.id !== id) currentOwner.close();
  currentOwner = {id, close};
  notify();
}

export function releaseOverlayPanel(id: string): void {
  if (currentOwner?.id === id) {
    currentOwner = null;
    notify();
  }
}

/** Reactive read of the current owner id, or null. Not required for eviction
 *  (which is push-based) — for future consumers that need to know "is some
 *  other panel open right now" (e.g. to disable a trigger). */
export function useActiveOverlayPanelId(): string | null {
  return useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => {
        listeners.delete(onStoreChange);
      };
    },
    () => currentOwner?.id ?? null
  );
}

/** Claims `id` as the active overlay owner whenever `isOpen` is true, and
 *  releases on close/unmount. `close` must be a stable callback (wrap the
 *  caller's handler in `useCallback`) — an identity change re-fires the
 *  effect (harmless but noisy: re-claims and re-notifies on every render). */
export function useOverlayPanelClaim(
  id: string,
  isOpen: boolean,
  close: CloseFn
): void {
  useEffect(() => {
    if (!isOpen) return;
    claimOverlayPanel(id, close);
    return () => {
      releaseOverlayPanel(id);
    };
  }, [id, isOpen, close]);
}
